import { Drawer, Slider, Stack, Switch, Text, Title } from '@mantine/core';

import { useChartStore } from '@/store/chartStore';
import type { ChartData } from '@/types/chart';

import ColorSchemeSelector from './ColorSchemeSelector';

interface ConfigDrawerProps {
  opened: boolean;
  onClose: () => void;
  chartData: ChartData | null;
}

export default function ConfigDrawer({ opened, onClose, chartData }: ConfigDrawerProps) {
  const useLogScale = useChartStore((state) => state.config.useLogScale);
  const setUseLogScale = useChartStore((state) => state.setUseLogScale);
  const showValues = useChartStore((state) => state.config.showValues);
  const setShowValues = useChartStore((state) => state.setShowValues);
  const maxHeight = useChartStore((state) => state.config.maxHeight);
  const setMaxHeight = useChartStore((state) => state.setMaxHeight);
  const invertLabelColors = useChartStore((state) => state.config.invertLabelColors);
  const setInvertLabelColors = useChartStore((state) => state.setInvertLabelColors);

  return (
    <Drawer opened={opened} onClose={onClose} title="Chart Configuration" position="right" size="md">
      <Stack gap="lg">
        {chartData?.description && (
          <div>
            <Title order={3} size="h5" mb="sm">
              Description
            </Title>
            <Text size="sm" c="dimmed">
              {chartData.description}
            </Text>
          </div>
        )}

        <div>
          <Title order={3} size="h5" mb="sm">
            Appearance
          </Title>
          <ColorSchemeSelector />
        </div>

        <div>
          <Title order={3} size="h5" mb="sm">
            Display
          </Title>
          <Stack gap="sm">
            <Switch
              label="Show values on bars"
              description="Display numeric values alongside hand labels"
              checked={showValues}
              onChange={(e) => setShowValues(e.currentTarget.checked)}
            />
            <Switch
              label="Logarithmic height scale"
              description="Better for data with wide value ranges"
              checked={useLogScale}
              onChange={(e) => setUseLogScale(e.currentTarget.checked)}
            />
            <Switch
              label="Dark labels"
              description="Dark text with light outline (better on mobile)"
              checked={invertLabelColors}
              onChange={(e) => setInvertLabelColors(e.currentTarget.checked)}
            />
            <div>
              <Text size="sm" fw={500} mb={4}>
                Max bar height
              </Text>
              <Slider
                value={maxHeight}
                onChange={setMaxHeight}
                min={1}
                max={10}
                step={0.5}
                marks={[
                  { value: 1, label: '1' },
                  { value: 5, label: '5' },
                  { value: 10, label: '10' },
                ]}
                label={(value) => value.toFixed(1)}
              />
            </div>
          </Stack>
        </div>
      </Stack>
    </Drawer>
  );
}
